/**
 * Test Lab UI types (T10 — plan §10.2).
 *
 * Mirrors :class:`api.routes.test_runs.TestRunSummary` /
 * :class:`TestRunRecord` so a backend literal-typed enum addition
 * surfaces here as a TS error rather than a silent ``any``.
 */

export type LabScenarioId =
  | 'intake-smoke'
  | 'full-pipeline'
  | 'gate-rewind'
  | 'compliance-export'
  | 'perf'
  | 'a11y'
  | 'custom'

export type LabPersonaId = 'dr-clinician' | 'pm-saas' | 'gov-defense' | 'custom'

export type LabRunMode = 'replay' | 'record' | 'live'

export type LabRunStatus =
  | 'queued'
  | 'running'
  | 'passed'
  | 'failed'
  | 'stopped'

export interface LabCaptureFlags {
  devtools_sidecar: boolean
  per_step_screenshots: boolean
  performance_trace: boolean
  lighthouse_audit: boolean
}

/** Compact row returned by ``GET /api/test-runs``. */
export interface LabRunSummary {
  id: string
  scenario: LabScenarioId
  persona: LabPersonaId
  mode: LabRunMode
  status: LabRunStatus
  started_at: string
  finished_at: string | null
}

/** Full record returned by ``GET /api/test-runs/{id}``. */
export interface LabRunRecord extends LabRunSummary {
  capture: LabCaptureFlags
  grep: string | null
  error_message: string | null
}

/** Body for ``POST /api/test-runs``. */
export interface StartLabRunRequest {
  scenario: LabScenarioId
  persona: LabPersonaId
  mode?: LabRunMode
  capture?: Partial<LabCaptureFlags>
  grep?: string | null
}

const TERMINAL: ReadonlySet<LabRunStatus> = new Set([
  'passed',
  'failed',
  'stopped',
])

export function isTerminalStatus(status: LabRunStatus): boolean {
  return TERMINAL.has(status)
}

/**
 * Compute ``finished_at - started_at`` in seconds when both are
 * present. Returns ``null`` for an in-flight run so the table can
 * render a "—" rather than a 0s placeholder.
 */
export function durationSeconds(run: LabRunSummary): number | null {
  if (!run.finished_at) return null
  const dt = Date.parse(run.finished_at) - Date.parse(run.started_at)
  if (Number.isNaN(dt)) return null
  return Math.max(0, Math.round(dt / 1000))
}
